import { isCompanyNif } from './nif';

/**
 * Avalia se um método de pagamento está disponível para o contexto actual
 * (total da encomenda + NIF da morada de facturação). Função pura partilhada
 * pelos 2 fluxos de checkout (MY + CRM) — substitui o `getRule` antes
 * enterrado no `PaymentMethodSelector` do MY.
 *
 * Regras espelhadas do shop legacy IHTP:
 *   - Cofidis Pay (CP): 60 € ≤ total ≤ 2500 € **E** NIF particular.
 *   - Outros métodos: respeitam `ValorMinimo` (campo do SP, vem em €).
 *
 * @returns {null | { hidden:boolean, reason:string }}
 *   - `null` → método aceitável, sem restrição
 *   - `hidden:true` → não aparece de todo (regra estrita: NIF empresa para CP)
 *   - `hidden:false` → aparece desactivado com a `reason` exibida
 */
export function evaluatePaymentMethodRule(method, { total = 0, billingNif = '' } = {}) {
  const id = method.ID_TipoPagamento ?? method.Id;
  const min = Number(method.ValorMinimo || 0);
  const t = Number(total || 0);

  if (min > 0 && t < min) {
    return { hidden: false, reason: `Valor mínimo: ${formatEuro(min)}` };
  }

  if (id === 'CP') {
    if (t < 60) return { hidden: true, reason: 'Disponível apenas para encomendas ≥ 60 €.' };
    if (t > 2500) return { hidden: true, reason: 'Disponível apenas para encomendas ≤ 2500 €.' };
    if (isCompanyNif(billingNif)) {
      return { hidden: true, reason: 'Não disponível para NIFs de empresa.' };
    }
  }

  return null;
}

/**
 * Filtra a lista de métodos de pagamento, removendo os que estão
 * estritamente bloqueados (`hidden:true`) e anotando os restantes com a
 * regra encontrada (para poderem ser desactivados visualmente sem somerem).
 *
 * @returns {Array<{ method, rule }>}
 */
export function filterPaymentMethods(methods, ctx) {
  return (methods || [])
    .filter((m) => (m.ID_TipoPagamento ?? m.Id) !== 'NA')
    .map((m) => ({ method: m, rule: evaluatePaymentMethodRule(m, ctx) }))
    .filter(({ rule }) => !rule?.hidden);
}

function formatEuro(v) {
  return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v || 0);
}
